// Start-of-string or whitespace before `#` (not URL fragments / mid-token) — same class as AI-1490 slash commands.
// Six-digit only here: three-digit would chip issue refs like `#864`. Short hex is composer InputRule-only (`#RGB `).
const HEX_COLOR_IN_MESSAGE_RE = /(?:^|[\s])(#[0-9a-fA-F]{6}\b)/g;
const SHORT_HEX_DIGIT_COUNT = 3;
const VALID_HEX_DIGITS_RE = /^[0-9a-f]{3}$|^[0-9a-f]{6}$/i;

export const HEX_TOKEN_PREFIX = 'hex-';

export type HexColorTokenAttrs = {
  hex: string;
  id: string;
  name: string;
  thumbnailUrl: null;
  type: 'color';
};

export type HexColorPart = { kind: 'plain'; text: string } | { kind: 'hex'; hex: string; text: string };

const expandShortHex = (digits: string) =>
  digits
    .split('')
    .map((char) => char + char)
    .join('');

/** Returns null when `raw` is not 3 or 6 hex digits (keeps unresolvable `[ref:color:hex-…]` as literal text). */
export const hexColorTokenAttrsFromRaw = (raw: string): HexColorTokenAttrs | null => {
  const digits = (raw.startsWith('#') ? raw.slice(1) : raw).toLowerCase();

  if (!VALID_HEX_DIGITS_RE.test(digits)) {
    return null;
  }

  const expanded = digits.length === SHORT_HEX_DIGIT_COUNT ? expandShortHex(digits) : digits;
  const hex = `#${expanded}`;

  return {
    hex,
    id: `${HEX_TOKEN_PREFIX}${expanded}`,
    name: hex,
    thumbnailUrl: null,
    type: 'color',
  };
};

export const splitTextForHexColorChips = (text: string): HexColorPart[] => {
  const out: HexColorPart[] = [];
  let last = 0;

  for (const match of text.matchAll(HEX_COLOR_IN_MESSAGE_RE)) {
    const raw = match[1];

    if (raw === undefined) {
      continue;
    }

    const attrs = hexColorTokenAttrsFromRaw(raw);

    if (!attrs) {
      continue;
    }

    const index = match.index ?? 0;
    const hexStart = index + match[0].length - raw.length;

    if (hexStart > last) {
      out.push({ kind: 'plain', text: text.slice(last, hexStart) });
    }

    out.push({ hex: attrs.hex, kind: 'hex', text: raw });
    last = hexStart + raw.length;
  }

  if (last < text.length) {
    out.push({ kind: 'plain', text: text.slice(last) });
  }

  if (out.length === 0) {
    out.push({ kind: 'plain', text });
  }

  return out;
};
